import React from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import logo from '../assets/botibonga_logo.png';
import { trackEvent, appendUTMsToUrl } from '../services/analytics';

const APP_URL = 'https://app.botibonga.com';

function handlePrimaryClick() {
  trackEvent('cta_click', { location: 'hero', label: 'Get Started Free' });
  // Anchor href="#cta" handles the smooth-scroll natively.
}

function handleLoginClick(e) {
  trackEvent('cta_click', { location: 'hero', label: 'Log In' });
  const target = appendUTMsToUrl(APP_URL);
  if (target !== APP_URL) {
    e.currentTarget.href = target;
  }
}

const gradientText = {
  background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export default function Hero() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Section-specific orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl animate-float"
          style={{ background: 'radial-gradient(circle, #c7d2fe 0%, transparent 70%)', opacity: 'var(--orb-hero-1)' }}
        />
        <div
          className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full blur-3xl animate-float-delayed"
          style={{ background: 'radial-gradient(circle, #ddd6fe 0%, transparent 70%)', opacity: 'var(--orb-hero-2)' }}
        />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8 pt-8 sm:pt-12">
          <div className="p-4 sm:p-5 rounded-3xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', boxShadow: 'var(--shadow-card)' }}>
            <img src={logo} alt="Botibonga Studio" className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl" fetchPriority="high" loading="eager" />
          </div>
        </div>

        {/* H1 Headline */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
          style={{ color: 'var(--text-heading)' }}>
          Create Your Own{' '}
          <span style={gradientText}>AI Assistant</span>{' '}
          for WhatsApp
        </h1>

        {/* Subheading */}
        <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          style={{ color: 'var(--text-body)' }}>
          No coding required. Build, train, and deploy your business chatbot in minutes, not months.
          Reply to customers instantly, capture leads, and book appointments automatically.
        </p>

        {/* CTA buttons — primary scrolls to the form in the CTA section */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#cta"
            onClick={handlePrimaryClick}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: 'var(--shadow-btn)',
            }}
          >
            Get Started Free
            <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href={APP_URL}
            onClick={handleLoginClick}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            style={{
              color: 'var(--text-accent)',
              border: '1px solid var(--border-btn)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            Log In
          </a>
        </div>

        {/* Trust line */}
        <p className="mt-6 text-xs" style={{ color: 'var(--text-muted)' }}>
          No credit card required · Free to start · Set up in minutes
        </p>
      </div>

      {/* Scroll indicator */}
      <div className="hidden sm:flex absolute bottom-8 right-8 flex-col items-center gap-2 animate-scroll-bounce">
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Scroll to explore</span>
        <ChevronDown className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
      </div>
    </section>
  );
}
